/**
 * ADM-032 and ADM-033. Runs a candidate over the evaluation set and shows, per case, the generated
 * formula next to the approved one, with differences marked. Staff can rate a generated formula
 * good or bad, which adds the case to the set.
 */
import * as stylex from "@stylexjs/stylex";
import type { AdminCaseComparison, AdminEvaluationRun, AdminEvaluationSet, AdminFormulaLine } from "@lattice/core";
import { ROUTES } from "@lattice/core";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/space.stylex";
import { space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import { useState } from "react";
import { api } from "./api.ts";
import { publishNotice } from "./notice.ts";
import { FilterChip, FilterRow, PageHead, Panel, Section, Table } from "./layout.tsx";
import { useLoad } from "./load.ts";
import { ReasonForm } from "./ReasonForm.tsx";
import { Button, ErrorText, Select, Status } from "./ui.tsx";
import { refusal } from "./words.ts";
const styles = {
  stack: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "x15agyyk",
    $$css: true
  },
  run: {
    k1xSpc: "x78zum5",
    kwnvtZ: "x1a02dak",
    kGNEyG: "xuk3077",
    kOIVth: "x626il7",
    $$css: true
  },
  summary: {
    kogj98: "x1ghz6dp",
    kMwMTN: "x1jty25b",
    $$css: true
  },
  pair: {
    k1xSpc: "xrvj5dj",
    kumcoG: "xcjjqqt",
    kOIVth: "x626il7",
    $$css: true
  },
  list: {
    kB88ic: "xe8uvvx",
    kogj98: "x1ghz6dp",
    kmVPX3: "x1717udv",
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "xskm3c9",
    $$css: true
  },
  extra: {
    kMwMTN: "x156x1qg",
    k63SB2: "xqu410z",
    $$css: true
  },
  missing: {
    kMwMTN: "x82pncc",
    k63SB2: "xqu410z",
    $$css: true
  },
  dose: {
    kMwMTN: "x82pncc",
    k63SB2: "xqu410z",
    $$css: true
  },
  same: {
    kMwMTN: "xzb3lse",
    $$css: true
  },
  caseHead: {
    k1xSpc: "x78zum5",
    kwnvtZ: "x1a02dak",
    kjj79g: "x1qughib",
    kOIVth: "x626il7",
    kogj98: "x1ghz6dp",
    $$css: true
  },
  caseId: {
    k63SB2: "xqu410z",
    $$css: true
  }
};
function mark(line: AdminFormulaLine, side: "generated" | "approved", row: AdminCaseComparison): "extra" | "missing" | "dose" | "same" {
  const disagreed = row.doseDisagreements.some(item => item.ingredientId === line.ingredientId);
  if (disagreed) return "dose";
  if (side === "generated" && row.extra.includes(line.ingredientId)) return "extra";
  if (side === "approved" && row.missing.includes(line.ingredientId)) return "missing";
  return "same";
}
function Formula({
  title,
  lines,
  side,
  row
}: {
  readonly title: string;
  readonly lines: readonly AdminFormulaLine[];
  readonly side: "generated" | "approved";
  readonly row: AdminCaseComparison;
}) {
  return <div>
      <h3 className="xqu410z">{title}</h3>
      {lines.length === 0 ? <p className="x1ghz6dp x1jty25b">None</p> : <ul className="xe8uvvx x1ghz6dp x1717udv x78zum5 xdt5ytf xskm3c9">
          {lines.map(line => <li key={line.ingredientId} {...stylex.props(styles[mark(line, side, row)])}>
              {line.name} {line.dose} {line.unit}
            </li>)}
        </ul>}
    </div>;
}
function generatedJson(row: AdminCaseComparison): string {
  return JSON.stringify({
    components: row.generated.map(line => ({
      ingredientId: line.ingredientId,
      dose: line.dose
    }))
  });
}
function RatePanel({
  row
}: {
  readonly row: AdminCaseComparison;
}) {
  const [label, setLabel] = useState<"good" | "bad" | null>(null);
  async function rate(reason: string): Promise<string | null> {
    if (label === null) return "Pick good or bad first.";
    const result = await api<AdminEvaluationSet>(ROUTES.adminRateEvaluation, {
      body: {
        caseId: row.caseId,
        label,
        reason,
        asNew: true,
        generatedJson: generatedJson(row)
      }
    });
    if (!result.ok) return refusal(result.error);
    publishNotice("saved", "Saved. This case is in the set.");
    return null;
  }
  if (label === null) return <div className="x78zum5 x1a02dak xuk3077 x626il7">
        <Button tone="quiet" onClick={() => setLabel("good")}>
          Rate as good
        </Button>
        <Button tone="quietDanger" onClick={() => setLabel("bad")}>
          Rate as bad
        </Button>
      </div>;
  return <ReasonForm id={`rate-${row.caseId}`} hint="A new evaluation case is stored from this generated formula." action={label === "good" ? "Rate as good" : "Rate as bad"} tone={label === "good" ? "primary" : "danger"} onSubmit={rate} onCancel={() => setLabel(null)} />;
}
function CaseCard({
  row,
  onRated
}: {
  readonly row: AdminCaseComparison;
  readonly onRated: () => void;
}) {
  return <Panel padded>
      <div className="x78zum5 xdt5ytf x15agyyk">
        <p className="x78zum5 x1a02dak x1qughib x626il7 x1ghz6dp">
          <span className="xqu410z">{row.caseId}</span>
          {row.label === "good" ? <Status tone="ok">Good</Status> : <Status tone="danger">Bad</Status>}
        </p>
        <p className="x1ghz6dp x1jty25b">{row.reason}</p>
        <div className="xrvj5dj xcjjqqt x626il7">
          <Formula title="Generated" lines={row.generated} side="generated" row={row} />
          <Formula title="Approved" lines={row.approved} side="approved" row={row} />
        </div>
        <RatePanel row={row} />
        <Button tone="quiet" onClick={onRated}>
          Clear this run
        </Button>
      </div>
    </Panel>;
}
function Run({
  set,
  onRan
}: {
  readonly set: AdminEvaluationSet;
  readonly onRan: (run: AdminEvaluationRun) => void;
}) {
  const live = set.versions.find(row => row.status === "live");
  const [versionId, setVersionId] = useState(set.versions.find(row => row.status === "candidate")?.id ?? live?.id ?? "");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function run() {
    if (versionId === "") return;
    setBusy(true);
    setError(null);
    publishNotice("saving", "Saving");
    const result = await api<AdminEvaluationRun>(ROUTES.adminEvaluate, {
      params: {
        versionId
      },
      body: {}
    });
    setBusy(false);
    if (!result.ok) {
      const message = refusal(result.error);
      setError(message);
      publishNotice("failed", message);
      return;
    }
    publishNotice("saved", "Saved");
    onRan(result.data);
  }
  return <div className="x78zum5 xdt5ytf x15agyyk">
      <div className="x78zum5 x1a02dak xuk3077 x626il7">
        <Select id="evaluation-version" label="Engine version" value={versionId} onChange={setVersionId} options={set.versions.map(row => ({
        value: row.id,
        label: `${row.version} (${row.status})`
      }))} />
        <Button onClick={() => void run()} disabled={busy || versionId === ""}>
          {busy ? "Saving" : "Run evaluation"}
        </Button>
      </div>
      {error === null ? null : <ErrorText>{error}</ErrorText>}
    </div>;
}
function Report({
  run,
  onRated
}: {
  readonly run: AdminEvaluationRun;
  readonly onRated: () => void;
}) {
  const gate = run.promoteReason === "no_new_safety_failures" ? "No new safety failures. This version may be promoted." : run.promoteReason === "no_cases" ? "The evaluation set is empty, so nothing is proved." : "New safety failures. Promotion is blocked.";
  return <div className="x78zum5 xdt5ytf x15agyyk">
      <Panel padded>
        <p className="x1ghz6dp x1jty25b">
          {run.version.version}: {run.caseCount} cases. Safety {run.safety.blocks} blocks,{" "}
          {run.safety.warnings} warnings. Agreement {run.agreement.sameIngredients} of{" "}
          {run.agreement.goodCases} good cases match ingredients. {gate}
        </p>
      </Panel>
      {run.cases.map(row => <Section key={row.caseId} title={row.caseId}>
          <CaseCard row={row} onRated={onRated} />
        </Section>)}
    </div>;
}
function Loaded({
  set
}: {
  readonly set: AdminEvaluationSet;
}) {
  const [run, setRun] = useState<AdminEvaluationRun | null>(null);
  const [view, setView] = useState<"cases" | "run">("cases");
  return <>
      <PageHead title="Evaluation" meta={`${set.cases.length} cases in the set`} />
      <FilterRow>
        <FilterChip to="/evaluation" label="Cases" count={set.cases.length} current={view === "cases"} onClick={() => setView("cases")} />
        <FilterChip to="/evaluation" label="Run" current={view === "run"} onClick={() => setView("run")} />
      </FilterRow>
      {view === "cases" ? <Table label="Evaluation cases" columns={[{
      label: "Case",
      cell: row => row.id
    }, {
      label: "Label",
      nowrap: true,
      cell: row => row.label === "good" ? <Status tone="ok">Good</Status> : <Status tone="danger">Bad</Status>
    }]} rows={set.cases} rowKey={row => row.id} empty="No evaluation cases yet. Rate a generated formula on Run to add one." /> : <>
          <Run set={set} onRan={next => {
        setRun(next);
      }} />
          {run === null ? <p className="x1ghz6dp x1jty25b">
              Choose a version and run it over the de-identified evaluation set. Each case shows the
              generated formula next to the approved one. Differences are marked.
            </p> : <Report run={run} onRated={() => setRun(null)} />}
        </>}
    </>;
}
export function Evaluation() {
  const [found] = useLoad("evaluation", () => api<AdminEvaluationSet>(ROUTES.adminEvaluation));
  if (found.state === "loading") return <PageHead title="Evaluation" meta="Loading" />;
  if (found.state === "failed") return <ErrorText>{refusal(found.error)}</ErrorText>;
  return <Loaded set={found.data} />;
}